// Ficha del documento tributario (pedido de Rodrigo, 2026-10-26: «las que mandan son los DTE; quiero
// ver la factura, reimprimirla, y me sale imprimir nota de venta»). Se abre desde Facturas / Boletas,
// el buscador (Ctrl+K), DTE › Documentos y la nota. Arriba el documento con sus acciones (ver,
// imprimir, cedible, XML); al centro su detalle tal como se emitió; al lado el cliente, el estado SII,
// el origen (la nota que reservó el stock, o «Punto de venta») y las NC/ND que lo corrigen.
import { useEffect, useRef, useState } from 'react';
import { PanelError } from '../comun/Estados';
import { decimal, fecha, fechaHora, pesos } from '../comun/formato';
import { CuerpoDoc, DatosDoc, EncabezadoDoc, Tarjeta } from '../compras/FichaDocumento';
import { useSesion } from '../sesion/SesionContexto';
import { textoEstado, tonoEstado, useFichaDocumento } from './documentosEmitidos';
import { TIPOS_INTERCAMBIO, XmlAlCliente } from './PantallasDte';
import { descargarXmlDte, imprimirDte, nombreDte, tituloDte, verPdfDte } from './pdfDte';

const SIGLA: Record<number, string> = {
  33: 'FAC',
  34: 'FEX',
  39: 'BOL',
  41: 'BEX',
  52: 'GD',
  56: 'ND',
  61: 'NC',
};
const CANAL: Record<string, string> = {
  MESON: 'Punto de venta',
  CALL_CENTER: 'Call center',
  VENDEDOR: 'Vendedor en ruta',
  B2B: 'Portal B2B',
};
const MEDIO_PAGO: Record<number, string> = {
  1: 'Efectivo',
  2: 'Pago electrónico',
  3: 'Transferencia',
  4: 'Cheque',
  5: 'Otro',
};

export function FichaDte({
  documentoId,
  alAbrirDte,
  alAbrirNota,
  alTitular,
}: {
  documentoId: string;
  alAbrirDte: (documentoId: string, titulo: string) => void;
  alAbrirNota?: ((notaId: string, numero: string) => void) | undefined;
  alTitular?: ((titulo: string) => void) | undefined;
}) {
  const { empresa, puede } = useSesion();
  const q = useFichaDocumento(empresa!.id, documentoId);
  const d = q.data;
  const [error, setError] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);
  const [xml, setXml] = useState(false);
  const tipoDte = d?.tipoDte;
  const folio = d?.folio;
  // La pestaña toma el nombre del documento («Factura N° 4978») una vez que se conoce.
  const titular = useRef(alTitular);
  titular.current = alTitular;
  useEffect(() => {
    if (tipoDte) titular.current?.(tituloDte(tipoDte, folio));
  }, [tipoDte, folio]);
  if (!d)
    return (
      <div className="pantalla">{q.isError ? <PanelError error={q.error} /> : 'Cargando…'}</div>
    );
  const titulo = tituloDte(d.tipoDte, d.folio);
  const emitido = d.estado === 'EMITIDO' && !!d.folio;
  const correr = (f: () => Promise<void>) => {
    setError(null);
    setOcupado(true);
    f()
      .catch((e: unknown) => setError(e))
      .finally(() => setOcupado(false));
  };
  const c = d.contenido;
  const t = c.totales;
  const o = d.origen;
  const lleva = [33, 34].includes(d.tipoDte);
  return (
    <div className="pantalla documento-compra doc-ficha" data-pantalla="ficha-dte">
      <EncabezadoDoc
        sigla={SIGLA[d.tipoDte] ?? 'DTE'}
        migas="Ventas › Facturas / Boletas"
        titulo={titulo}
        insignias={<span className={`insignia ${tonoEstado(d)}`}>{textoEstado(d)}</span>}
        sub={
          <>
            {d.receptorRazonSocial ?? '—'}
            {d.receptorRut ? ` · ${d.receptorRut}` : ''} · emitida el {fecha(d.fechaEmision)}
          </>
        }
        acciones={
          emitido && (
            <>
              <button
                type="button"
                disabled={ocupado}
                title="Abre el PDF en otra pestaña"
                onClick={() => correr(() => verPdfDte(d.documentoId))}
              >
                Ver PDF
              </button>
              <button
                type="button"
                className="primario"
                disabled={ocupado}
                title={
                  lleva
                    ? 'Directo a la impresora: original y cedible'
                    : 'Directo a la impresora: dos copias'
                }
                onClick={() => correr(() => imprimirDte(d.documentoId))}
              >
                Imprimir
              </button>
              {lleva && (
                <button
                  type="button"
                  disabled={ocupado}
                  onClick={() => correr(() => verPdfDte(d.documentoId, 'cedible'))}
                >
                  Cedible
                </button>
              )}
              <button
                type="button"
                disabled={ocupado}
                onClick={() => correr(() => descargarXmlDte(d.documentoId, d.tipoDte, d.folio))}
              >
                XML
              </button>
              {TIPOS_INTERCAMBIO.includes(d.tipoDte) && (
                <button type="button" onClick={() => setXml(true)}>
                  XML al cliente
                </button>
              )}
            </>
          )
        }
      />
      {!!error && <PanelError error={error} />}
      <CuerpoDoc
        principal={
          <>
            <Tarjeta
              titulo="Detalle"
              etiqueta="Detalle del documento"
              extra={`${c.lineas.length} línea(s)`}
            >
              <table className="grilla compacta" aria-label={`Detalle de ${titulo}`}>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Código</th>
                    <th>Descripción</th>
                    <th className="der">Cantidad</th>
                    <th>Unidad</th>
                    <th className="der">Precio</th>
                    <th className="der">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {c.lineas.map((l) => (
                    <tr key={l.nro}>
                      <td>{l.nro}</td>
                      <td className="mono">{l.codigo}</td>
                      <td>{l.nombre}</td>
                      <td className="der">{decimal(l.cantidad)}</td>
                      <td>{l.unidad}</td>
                      <td className="der">{pesos(l.precioUnitario)}</td>
                      <td className="der">{pesos(l.montoItem)}</td>
                    </tr>
                  ))}
                  {!c.lineas.length && (
                    <tr>
                      <td colSpan={7} className="grilla-vacia">
                        Sin detalle
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </Tarjeta>
            {c.referencias.length > 0 && (
              <Tarjeta titulo="Referencias" etiqueta="Referencias del documento">
                <table className="grilla compacta" aria-label="Referencias">
                  <thead>
                    <tr>
                      <th>Documento</th>
                      <th>Folio</th>
                      <th>Fecha</th>
                      <th>Razón</th>
                    </tr>
                  </thead>
                  <tbody>
                    {c.referencias.map((r) => (
                      <tr key={r.nro}>
                        <td>{nombreDte(Number(r.tipoDocumento)) ?? r.tipoDocumento}</td>
                        <td className="mono">{r.folio}</td>
                        <td>{fecha(r.fecha)}</td>
                        <td>{r.razon ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Tarjeta>
            )}
            {d.ajustes.length > 0 && (
              <Tarjeta titulo="Notas de crédito y débito" etiqueta="Correcciones del documento">
                <table className="grilla compacta" aria-label="Notas de crédito y débito">
                  <thead>
                    <tr>
                      <th>Documento</th>
                      <th>Fecha</th>
                      <th>Motivo</th>
                      <th>Estado</th>
                      <th className="der">Monto</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {d.ajustes.map((a) => (
                      <tr key={a.ajusteId}>
                        <td>
                          {a.tipoDte
                            ? tituloDte(a.tipoDte, a.folio)
                            : a.tipo === 'NC'
                              ? 'Nota de crédito (por emitir)'
                              : 'Nota de débito (por emitir)'}
                        </td>
                        <td>{fecha(a.creadoEn)}</td>
                        <td>{a.descripcion}</td>
                        <td>{a.estado.toLowerCase().replace(/_/g, ' ')}</td>
                        <td className="der">{pesos(a.bruto)}</td>
                        <td>
                          {a.documentoId && (
                            <button
                              type="button"
                              className="boton-mini"
                              onClick={() =>
                                alAbrirDte(a.documentoId!, tituloDte(a.tipoDte, a.folio))
                              }
                            >
                              Abrir
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Tarjeta>
            )}
          </>
        }
        lateral={
          <>
            <Tarjeta titulo="Totales" etiqueta="Totales">
              <DatosDoc
                datos={[
                  t && Number(t.neto) > 0 && { e: 'Neto', v: pesos(t.neto) },
                  t && Number(t.exento) > 0 && { e: 'Exento', v: pesos(t.exento) },
                  t && Number(t.iva) > 0 && { e: 'IVA', v: pesos(t.iva) },
                  ...(t?.impuestosAdicionales ?? []).map(
                    (i, k) =>
                      Number(i.monto) > 0 && {
                        e: i.nombre ?? `Impuesto ${i.codigo ?? k + 1}`,
                        v: pesos(i.monto),
                      },
                  ),
                  { e: 'Total', v: pesos(t?.total ?? d.total), fuerte: true },
                ]}
              />
            </Tarjeta>
            <Tarjeta titulo="Cliente" etiqueta="Cliente">
              <DatosDoc
                datos={[
                  { e: 'Razón social', v: c.receptor?.razonSocial ?? d.receptorRazonSocial ?? '—' },
                  { e: 'RUT', v: c.receptor?.rut ?? d.receptorRut ?? '—', mono: true },
                  c.receptor?.giro && { e: 'Giro', v: c.receptor.giro },
                  c.receptor?.direccion && {
                    e: 'Dirección',
                    v: `${c.receptor.direccion}${c.receptor.comuna ? `, ${c.receptor.comuna}` : ''}`,
                  },
                ]}
              />
            </Tarjeta>
            <Tarjeta titulo="Venta" etiqueta="Datos de la venta">
              <DatosDoc
                datos={[
                  {
                    e: 'Origen',
                    v:
                      o?.tipo === 'VENTA_DIRECTA' ? (
                        (CANAL[o.canal] ?? 'Venta directa')
                      ) : o?.tipo === 'NOTA' ? (
                        <>
                          {CANAL[o.canal] ?? o.canal} ·{' '}
                          {alAbrirNota ? (
                            <button
                              type="button"
                              className="enlace mono"
                              title="La nota de venta que reservó el stock"
                              onClick={() => alAbrirNota(o.notaId, o.numeroVisible)}
                            >
                              {o.numeroVisible}
                            </button>
                          ) : (
                            <span className="mono">{o.numeroVisible}</span>
                          )}
                        </>
                      ) : o?.tipo === 'AJUSTE' && o.referido ? (
                        <button
                          type="button"
                          className="enlace"
                          onClick={() =>
                            alAbrirDte(
                              o.referido!.documentoId,
                              tituloDte(o.referido!.tipoDte, o.referido!.folio),
                            )
                          }
                        >
                          Corrige {tituloDte(o.referido.tipoDte, o.referido.folio)}
                        </button>
                      ) : (
                        '—'
                      ),
                  },
                  c.impresion?.vendedor && { e: 'Vendedor', v: c.impresion.vendedor },
                  c.impresion?.condicion && { e: 'Condición', v: c.impresion.condicion },
                  !!c.formaPago && {
                    e: 'Forma de pago',
                    v: c.formaPago === 1 ? 'Contado' : 'Crédito',
                  },
                  !!c.medioPago && { e: 'Medio de pago', v: MEDIO_PAGO[c.medioPago] ?? '—' },
                  c.impresion?.fechaVencimiento && {
                    e: 'Vence',
                    v: fecha(c.impresion.fechaVencimiento),
                  },
                  c.impresion?.observaciones && {
                    e: 'Observaciones',
                    v: c.impresion.observaciones,
                  },
                ]}
              />
            </Tarjeta>
            <Tarjeta titulo="SII" etiqueta="Estado en el SII">
              <DatosDoc
                datos={[
                  { e: 'Estado', v: textoEstado(d) },
                  d.trackId && { e: 'Track ID', v: d.trackId, mono: true },
                  { e: 'Registrado', v: fechaHora(d.creadoEn) },
                ]}
              />
            </Tarjeta>
            {o?.tipo === 'VENTA_DIRECTA' && alAbrirNota && puede('ventas.nota.ver') && (
              <p className="ayuda">
                Trazabilidad:{' '}
                <button
                  type="button"
                  className="enlace"
                  title="El registro interno con que se reservó y descontó el stock"
                  onClick={() => alAbrirNota(o.notaId, nombreDte(d.tipoDte))}
                >
                  ver el registro interno de la venta
                </button>
              </p>
            )}
          </>
        }
      />
      {xml && (
        <XmlAlCliente
          documento={{
            id: d.documentoId,
            tipo_dte: d.tipoDte,
            folio: d.folio ? Number(d.folio) : null,
            estado: d.estado,
            estado_envio: d.estadoEnvio ?? '',
            estado_sii: d.estadoSii ?? '',
            monto_total: d.total,
            fecha_emision: d.fechaEmision,
            origen_id: o?.notaId ?? '',
            creado_en: d.creadoEn,
            receptor_rut: d.receptorRut,
            receptor_razon_social: d.receptorRazonSocial,
          }}
          puedeEnviar={puede('dte.intercambio.enviar')}
          alCerrar={() => setXml(false)}
        />
      )}
    </div>
  );
}
